import React, { useState, useRef, useMemo, useCallback, lazy, Suspense } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, Link } from '@mui/material';
import FormTextField from './FormTextField';

const Turnstile = lazy(() => import('@marsidev/react-turnstile').then((mod) => ({ default: mod.Turnstile })));
import FormSelectField from './FormSelectField';
import CheckboxOption from './CheckboxOption';
import { REACT_APP_API_GATEWAY_URL_CONTACT, REACT_APP_TURNSTILE_SITE_KEY } from '../../constants/config';

const INITIAL_FORM_DATA = {
  firstName: '',
  lastName: '',
  jobTitle: '',
  email: '',
  company: '',
  industry: '',
  employeeHeadcount: '',
};

const HELP_OPTIONS = [
  { name: 'Founding_Partner_Program', label: 'Participate in the Founding Partner Program' },
  { name: 'EU_PTD_Prep', label: 'Prepare for the EU Pay Transparency Directive' },
  { name: 'Global_Pay_Equity_Reporting', label: 'Meet Global Pay Equity Reporting Requirements' },
  { name: 'First_Pay_Equity_Analysis', label: 'Run our first pay equity analysis' },
  { name: 'Learning_More', label: 'Learn about AI-native pay transparency / equity' },
];

const INDUSTRIES = [
  'Business Products & Services (Non-tech)',
  'Consumer Products & Services (Non-tech)',
  'Energy',
  'Financial Services & Insurance',
  'Healthcare',
  'Hospitality',
  'Life Sciences',
  'Manufacturing & Industrial Goods',
  'Media, Entertainment & Gaming',
  'Professional Services',
  'Retail & Ecommerce',
  'Technology – Hardware & Devices',
  'Technology – Software Products & Services',
  'Transportation & Logistics',
  'Other',
];

const HEADCOUNTS = [
  '1 to 50 employees',
  '51 to 250 employees',
  '251 to 500 employees',
  '501 to 1,000 employees',
  '1,001 to 5,000 employees',
  '5,001 to 10,000 employees',
  '10,000+ employees',
];

const INITIAL_HELP_OPTIONS = HELP_OPTIONS.reduce((acc, option) => {
  acc[option.name] = false;
  return acc;
}, {});

const RequestDemo = () => {
  const [formData, setFormData] = useState({
    ...INITIAL_FORM_DATA,
    helpOptions: {
      ...INITIAL_HELP_OPTIONS,
    },
  });
  const [feedback, setFeedback] = useState({ message: '', type: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const initialFormData = useMemo(
    () => ({
      ...INITIAL_FORM_DATA,
      helpOptions: {
        ...INITIAL_HELP_OPTIONS,
      },
    }),
    [],
  );

  // Memoize handlers
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        helpOptions: {
          ...prev.helpOptions,
          [name]: checked,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  }, []);

  const handleTurnstileSuccess = useCallback((token) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleTurnstileExpired = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!turnstileToken) {
        setFeedback({
          message: 'Please complete the verification',
          type: 'error',
        });
        return;
      }

      setIsSubmitting(true);
      setFeedback({ message: 'Submitting request...', type: 'info' });

      try {
        const response = await fetch(REACT_APP_API_GATEWAY_URL_CONTACT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...formData,
            turnstileToken,
            action: 'contact',
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Server error');
        }

        const responseData = await response.json();
        if (!responseData.success) {
          throw new Error(responseData.message);
        }

        // Reset form
        setFormData(initialFormData);
        setTurnstileToken(null);
        if (turnstileRef.current) {
          turnstileRef.current.reset();
        }

        setFeedback({
          message: "Thank you! We've received your request and will be in touch shortly.",
          type: 'success',
        });
      } catch (error) {
        console.error('Error submitting form:', error);
        setFeedback({
          message: error.message || 'There was an error submitting your request. Please try again.',
          type: 'error',
        });
        // Reset turnstile on error
        if (turnstileRef.current) {
          turnstileRef.current.reset();
        }
        setTurnstileToken(null);
      } finally {
        setIsSubmitting(false);
      }
    },
    [turnstileToken, formData, initialFormData],
  );

  // Memoize checkbox change handler
  const handleCheckboxChange = useCallback(
    (name) => (e) => {
      handleChange({
        ...e,
        target: {
          ...e.target,
          name,
          type: 'checkbox',
          checked: e.target.checked,
        },
      });
    },
    [handleChange],
  );

  return (
    <Box sx={{ width: '100vw', bgcolor: 'white' }}>
      <Box
        sx={{
          maxWidth: '1120px',
          mx: 'auto',
          pt: 16,
          pb: 10,
          px: 2,
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '2.5rem', sm: '3.75rem' },
            fontWeight: 400,
            textAlign: 'center',
            mb: { xs: 2.5, md: 6 },
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          Take control of your pay gaps <span style={{ color: '#4f46e5', fontWeight: 500 }}>without overspending</span>
        </Typography>

        <Box
          sx={{
            maxWidth: '800px',
            mx: 'auto',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', md: '1.125rem' },
              mb: { xs: 1, md: 5 },
              color: 'text.secondary',
              textAlign: 'center',
              lineHeight: 1.6,
            }}
          >
            Tell us more about you, and we&apos;ll be in touch shortly.
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              bgcolor: '#fff',
              p: { xs: 4, md: 5 },
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              width: '100%',
              boxSizing: 'border-box',
              '@media (max-width: 768px)': {
                border: 'none',
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  sx={{
                    flex: { xs: '1 1 100%', sm: '1 1 0' },
                    minWidth: 0,
                    width: { xs: '100%', sm: 'calc(50% - 8px)' },
                  }}
                >
                  <FormTextField
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="* First Name"
                  />
                </Box>
                <Box
                  sx={{
                    flex: { xs: '1 1 100%', sm: '1 1 0' },
                    minWidth: 0,
                    width: { xs: '100%', sm: 'calc(50% - 8px)' },
                  }}
                >
                  <FormTextField
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="* Last Name"
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  sx={{
                    flex: { xs: '1 1 100%', sm: '1 1 0' },
                    minWidth: 0,
                    width: { xs: '100%', sm: 'calc(50% - 8px)' },
                  }}
                >
                  <FormTextField
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    placeholder="* Job Title"
                  />
                </Box>
                <Box
                  sx={{
                    flex: { xs: '1 1 100%', sm: '1 1 0' },
                    minWidth: 0,
                    width: { xs: '100%', sm: 'calc(50% - 8px)' },
                  }}
                >
                  <FormTextField
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="* Company Name"
                  />
                </Box>
              </Box>
            </Box>

            <FormTextField
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="* Business Email Address"
            />

            <Box
              sx={{
                display: 'flex',
                gap: 3,
                flexDirection: { xs: 'column', sm: 'row' },
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <FormSelectField
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  placeholder="* Industry"
                  options={INDUSTRIES}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                <FormSelectField
                  name="employeeHeadcount"
                  value={formData.employeeHeadcount}
                  onChange={handleChange}
                  placeholder="* Employee Headcount"
                  options={HEADCOUNTS}
                />
              </Box>
            </Box>

            <Box sx={{ mt: 1, mb: 0, ml: 2, textAlign: 'left', width: '100%' }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: '#0f172a',
                  mb: 2,
                  fontSize: '0.875rem',
                }}
              >
                * How can Sophare help you?
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {HELP_OPTIONS.map((option) => (
                  <CheckboxOption
                    key={option.name}
                    name={option.name}
                    checked={formData.helpOptions[option.name]}
                    onChange={handleCheckboxChange(option.name)}
                    label={option.label}
                  />
                ))}
              </Box>
            </Box>

            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem',
                color: '#000',
                my: 1,
                lineHeight: 1.5,
              }}
            >
              By submitting this form, you consent to receive commercial communications from Sophare pursuant to our{' '}
              <Link
                href={`${process.env.REACT_APP_DOCS_URL}/resources`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'inherit', textDecoration: 'none' }}
              >
                privacy policy
              </Link>
              .
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                my: 1,
                minHeight: '65px',
              }}
            >
              <Suspense
                fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                }
              >
                <Turnstile
                  ref={turnstileRef}
                  siteKey={REACT_APP_TURNSTILE_SITE_KEY}
                  onSuccess={handleTurnstileSuccess}
                  onError={handleTurnstileError}
                  onExpire={handleTurnstileExpired}
                />
              </Suspense>
            </Box>

            {feedback.message && (
              <Alert
                severity={feedback.type === 'error' ? 'error' : feedback.type === 'success' ? 'success' : 'info'}
                sx={{
                  borderRadius: 2,
                }}
              >
                {feedback.message}
              </Alert>
            )}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting || !turnstileToken}
                sx={{
                  py: 1.5,
                  px: { xs: 6, md: 8 },
                  minWidth: { xs: 220, md: 280 },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  backgroundColor: '#0f172a',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#1e293b',
                  },
                  '&:disabled': {
                    backgroundColor: '#e2e8f0',
                    color: '#94a3b8',
                  },
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Schedule Demo'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RequestDemo;
